import { ChangeDetectionStrategy, Component, Injector, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { LogRecord } from '../../../common/model/log-record';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { dateTimeString } from '../../../core/helper/date-time';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { StandardCommandHandlerResolver } from '../../../core/service/command/standard-command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ColumnDefinitionService } from '../../../rent/service/data/column-definition.service';
import { Article } from '../../../rent/model/article';
import { ArticleService } from '../../../rent/service/data/article.service';
import {
  AmortizationPlan,
  AmortizationPlanInvoice,
  AmortizationPlanPaymentNotice,
  AmortizationPlanPaymentNoticeDelivery,
} from '../../model/amortization-plan';
import { AmortizationPlanCreateDefaults, AmortizationPlanLeaseAgreementOption } from '../../model/amortization-plan-create';
import {
  AmortizationPlanStatus,
  InvoiceDeliveryMethod,
  PaymentNoticeDeliveryStatus,
  PaymentNoticeDeliveryStatusToString,
  ReminderDeliveryMethod,
  ReminderDeliveryMethodToString,
} from '../../model/const/enum';
import { AmortizationPlanDocumentFormComponent } from '../amortization-plan-document-form/amortization-plan-document-form.component';
import { AmortizationPlanDocumentManagerComponent } from '../amortization-plan-document-manager/amortization-plan-document-manager.component';
import { CancelAmortizationPlanFormComponent } from '../cancel-amortization-plan-form/cancel-amortization-plan-form.component';
import { RegisterPaymentNoticePaymentFormComponent } from '../register-payment-notice-payment-form/register-payment-notice-payment-form.component';
import { AmortizationPlanLogService } from '../../service/data/amortization-plan-log.service';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';
import { LanguageService } from '../../../core/service/language.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
  standalone: false,
  selector: 'app-amortization-plan-details',
  templateUrl: './amortization-plan-details.component.html',
  styleUrls: ['./amortization-plan-details.component.scss'],
  providers: [{ provide: DataService, useExisting: AmortizationPlanService }],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AmortizationPlanDetailsComponent extends FormComponentBase<AmortizationPlan, AmortizationPlanService> {
  @ViewChild(AmortizationPlanDocumentManagerComponent) documentManager?: AmortizationPlanDocumentManagerComponent;

  invoiceColumnDefinition: ColumnDefinition[];
  installmentColumnDefinition: ColumnDefinition[];
  logColumnDefinition: ColumnDefinition[];
  logs: LogRecord[] = [];
  showInvoices: boolean;
  showPaymentNotices: boolean;
  showInstallments: boolean;
  articleSelection: SelectionItem<string>[] = [];
  leaseAgreementSelection: SelectionItem<string>[] = [];
  defaults: AmortizationPlanCreateDefaults;
  canManageAmortizationPlans = false;
  private _formStateSubscription = new Subscription();
  readonly paymentNoticeDeliveryMethods: InvoiceDeliveryMethod[] = [
    InvoiceDeliveryMethod.EmailWithPDF,
    InvoiceDeliveryMethod.Manual,
    InvoiceDeliveryMethod.Mail,
  ];
  private readonly postalAddressDeliveryMethods: InvoiceDeliveryMethod[] = [
    InvoiceDeliveryMethod.Manual,
    InvoiceDeliveryMethod.Mail,
  ];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _fc: FormControlExFactory,
    private readonly _languageService: LanguageService,
    private readonly _dialogService: ActionWindowService,
    private readonly _injector: Injector,
    private readonly _nativeWindow: NativeWindowService,
    private readonly _navigation: NavigationService,
    private readonly _router: Router,
    private readonly _articleService: ArticleService,
    accountService: UserAccountService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: StandardCommandHandlerResolver,
    service: AmortizationPlanService,
    messageService: MessageService,
    navigation: NavigationService,
    factory: ColumnDefinitionFactory,
    columnDefinitionService: ColumnDefinitionService,
    private readonly logService: AmortizationPlanLogService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.addDeleteMenu = false;

    this.invoiceColumnDefinition = [
      factory.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      factory.createDate('INVOICE.DUE_DATE', 'dueDate'),
      factory.createMoney('INVOICE.REMAINING_AMOUNT', 'remainingAmount'),
    ];

    this.installmentColumnDefinition = [
      factory.createDate('AMORTIZATION_PLAN.CREATE.INSTALLMENT_DUE_DATE', 'dueDate'),
      factory.create('AMORTIZATION_PLAN.CREATE.INSTALLMENT_NOTICE', 'notice'),
      factory.createMoney('AMORTIZATION_PLAN.CREATE.INSTALLMENT_AMOUNT', 'amount'),
      factory.createMoney('AMORTIZATION_PLAN.CREATE.INSTALLMENT_PAID_AMOUNT', 'paidAmount'),
      factory.createMoney('AMORTIZATION_PLAN.CREATE.INSTALLMENT_CREDITED_AMOUNT', 'creditedAmount'),
      factory.createMoney('AMORTIZATION_PLAN.CREATE.INSTALLMENT_CUSTOMER_LOSS_AMOUNT', 'customerLossAmount'),
    ];

    this.logColumnDefinition = columnDefinitionService.create('Log');

    this.AddSubscription(
      accountService.current.subscribe(account => {
        this.canManageAmortizationPlans = account.hasAccess(ModuleNames.Invoicing, Features.ManageInvoices);
      })
    );
  }

  protected createModel(): AmortizationPlan {
    return new AmortizationPlan();
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      companyName: this._fc.createReadonly('COMPANY.LIST_HEADER', this.dataContext.companyName),
      debtorContactName: this._fc.createReadonly('COMMON.CONTACT', this.dataContext.debtorContactName),
      leaseAgreementId: this._fc.create('AMORTIZATION_PLAN.CREATE.LEASE_AGREEMENT', this.dataContext.leaseAgreementId ?? ''),
      leaseAgreementDisplayName: this._fc.createReadonly('AMORTIZATION_PLAN.CREATE.LEASE_AGREEMENT', this.dataContext.leaseAgreementDisplayName),
      articleId: this._fc.createMandatory('AMORTIZATION_PLAN.INTEREST_ARTICLE', this.dataContext.articleId),
      articleCaption: this._fc.createReadonly('AMORTIZATION_PLAN.INTEREST_ARTICLE', this.dataContext.articleCaption),
      statusText: this._fc.createReadonly(
        'COMMON.STATUS',
        this.dataContext.statusCaption ? this._languageService.instant(this.dataContext.statusCaption) : null
      ),
      interestRate: this._fc.createWithOptions('AMORTIZATION_PLAN.INTEREST_RATE', this.dataContext.interestRate, {
        minValue: 0,
        required: true,
        isReadonly: !this.canEditInterestSettings,
      }),
      totalAmount: this._fc.createReadonly('COMMON.AMOUNT', this.dataContext.totalAmount),
      calculatedInterestAmount: this._fc.createReadonly(
        'AMORTIZATION_PLAN.CALCULATED_INTEREST_AMOUNT',
        this.dataContext.calculatedInterestAmount
      ),
      paymentNoticeDeliveryMethod: this._fc.createMandatory(
        'AMORTIZATION_PLAN.PAYMENT_NOTICE_DELIVERY_METHOD',
        this.dataContext.paymentNoticeDeliveryMethod
      ),
      interestInvoiceDelivery: this._fb.group({
        deliveryMethod: this._fc.create('AMORTIZATION_PLAN.INTEREST_INVOICE_DELIVERY_METHOD', this.dataContext.interestInvoiceDelivery?.deliveryMethod),
        email: this._fc.createEmail('COMMON.EMAIL', this.dataContext.interestInvoiceDelivery?.email),
        eInvoiceAddress: this._fc.create('TENANT.EINVOICE_ADDRESS', this.dataContext.interestInvoiceDelivery?.eInvoiceAddress),
        address: this._fb.group({
          name: this._fc.createWithOptions('ADDRESS_FORM.RECIPIENT_LABEL', this.dataContext.interestInvoiceDelivery?.address?.name, { length: 100 }),
          additionalName: this._fc.createMaxLength('ADDRESS_FORM.ADDRESS_ROW2', this.dataContext.interestInvoiceDelivery?.address?.additionalName, 50),
          street: this._fc.createWithOptions('ADDRESS_FORM.STREET_LABEL', this.dataContext.interestInvoiceDelivery?.address?.street, { length: 100 }),
          postalCode: this._fc.createWithOptions('ADDRESS_FORM.POSTAL_CODE_LABEL', this.dataContext.interestInvoiceDelivery?.address?.postalCode, {
            length: 20,
          }),
          city: this._fc.createWithOptions('ADDRESS_FORM.CITY_LABEL', this.dataContext.interestInvoiceDelivery?.address?.city, { length: 50 }),
          country: this._fc.createWithOptions('ADDRESS_FORM.COUNTRY_LABEL', this.dataContext.interestInvoiceDelivery?.address?.country, {
            length: 50,
          }),
        }),
      }),
      createdAt: this._fc.createReadonly('COMMON.CREATED_DATE', dateTimeString(this.dataContext.createdAt)),
      note: this._fc.createMaxLength('COMMON.NOTE', this.dataContext.note, 500),
    });
  }

  protected override prepareSave(): any {
    const paymentNoticeDeliveryMethod = this.form.controls['paymentNoticeDeliveryMethod'].value;

    return {
      id: this.dataContext.id,
      leaseAgreementId: this.canEditLeaseAgreement ? this.form.controls['leaseAgreementId'].value || null : this.dataContext.leaseAgreementId || null,
      articleId: this.canEditInterestSettings ? this.form.controls['articleId'].value : this.dataContext.articleId,
      interestRate: this.canEditInterestSettings ? this.form.controls['interestRate'].value : this.dataContext.interestRate,
      paymentNoticeDeliveryMethod,
      interestInvoiceDelivery: this.showInterestInvoiceDelivery
        ? this.toInterestInvoiceDeliverySaveModel(paymentNoticeDeliveryMethod)
        : null,
      note: this.form.controls['note'].value,
    };
  }

  protected override formSubmittedChange(): void {
    super.formSubmittedChange();
    this.AddSubscription(this.dataService.get(this.dataContext.id).subscribe(plan => this.refresh(plan)));
  }

  protected init(data: AmortizationPlan) {
    super.init(data);
    this.showInvoices = data.invoices?.length > 0;
    this.showPaymentNotices = data.paymentNotices?.length > 0;
    this.showInstallments = data.installments?.length > 0;
    if (this.canManageAmortizationPlans) {
      this.loadSelectionData();
      this.subscribeToFormStateChanges();
      this.updateInterestInvoiceDeliveryState();
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  override ngOnDestroy(): void {
    this._formStateSubscription.unsubscribe();
    super.ngOnDestroy();
  }

  protected override initializeSection(section: string) {
    switch (section) {
      case 'logs':
        this.AddSubscription(
          this.logService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
            this.logs = res.items;
          })
        );
        break;
    }

    super.initializeSection(section);
  }

  get canSendForDigitalSigning(): boolean {
    return this.canManageAmortizationPlans && !!this.dataContext?.id && !!this.dataContext?.canApprove;
  }

  get canGeneratePdf(): boolean {
    return !!this.dataContext?.id;
  }

  get canApprove(): boolean {
    return this.canManageAmortizationPlans && !!this.dataContext?.canApprove;
  }

  get canWithdrawApproval(): boolean {
    return this.canManageAmortizationPlans && !!this.dataContext?.canWithdrawApproval;
  }

  get canCancelPlan(): boolean {
    return this.canManageAmortizationPlans && !!this.dataContext?.canCancel;
  }

  get canEditInterestSettings(): boolean {
    return this.canManageAmortizationPlans && this.dataContext?.status === AmortizationPlanStatus.Draft;
  }

  get canEditLeaseAgreement(): boolean {
    return this.canManageAmortizationPlans && this.dataContext?.status === AmortizationPlanStatus.Draft;
  }

  get showSeparatePaymentNoticeInfo(): boolean {
    return !!this.dataContext && !this.canEditLeaseAgreement && !this.dataContext.leaseAgreementId;
  }

  get availableLeaseAgreements(): AmortizationPlanLeaseAgreementOption[] {
    const leaseAgreements = (this.defaults?.leaseAgreements ?? []).filter(leaseAgreement =>
      (!this.dataContext?.companyId || leaseAgreement.companyId === this.dataContext.companyId)
      && (!this.dataContext?.debtorContactId || leaseAgreement.payerContactId === this.dataContext.debtorContactId)
    );

    const selectedLeaseAgreementId = this.dataContext?.leaseAgreementId;
    if (!selectedLeaseAgreementId || leaseAgreements.some(item => item.id === selectedLeaseAgreementId)) {
      return leaseAgreements;
    }

    return [
      {
        id: selectedLeaseAgreementId,
        companyId: this.dataContext.companyId,
        companyName: this.dataContext.companyName,
        leaseAgreementNumber: this.dataContext.leaseAgreementNumber,
        status: null,
        category: null,
        categoryCaption: null,
        rentalObjectCaption: null,
        payerName: this.dataContext.debtorContactName,
        payerContactId: this.dataContext.debtorContactId,
        tenantNames: null,
        displayName: this.dataContext.leaseAgreementDisplayName || this.dataContext.leaseAgreementNumber || selectedLeaseAgreementId,
        firstRentPeriods: [],
      },
      ...leaseAgreements,
    ];
  }

  get showCalculatedInterestAmount(): boolean {
    return !!this.dataContext?.calculatedInterestAmount && !this.dataContext.calculatedInterestAmount.IsZero;
  }

  get showInterestInvoiceDelivery(): boolean {
    const interestRate = this.form?.controls?.['interestRate']?.value ?? this.dataContext?.interestRate;
    return typeof interestRate === 'number' ? interestRate !== 0 : !!interestRate;
  }

  get showSeparateInterestInvoiceDeliveryMethod(): boolean {
    return this.showInterestInvoiceDelivery && !this.useSharedDeliveryMethod;
  }

  get interestInvoiceDeliveryGroup(): FormGroup {
    return this.form.controls['interestInvoiceDelivery'] as FormGroup;
  }

  get interestInvoiceDeliveryAddressGroup(): FormGroup {
    return this.interestInvoiceDeliveryGroup.controls['address'] as FormGroup;
  }

  get interestInvoiceDeliveryAddressModel(): any {
    return this.dataContext?.interestInvoiceDelivery?.address ?? {
      name: null,
      additionalName: null,
      street: null,
      postalCode: null,
      city: null,
      country: 'SE',
    };
  }

  get selectedInterestInvoiceDeliveryMethod(): InvoiceDeliveryMethod | null {
    const value = this.interestInvoiceDeliveryGroup.controls['deliveryMethod'].value;
    return typeof value === 'number' ? value : null;
  }

  get selectedPaymentNoticeDeliveryMethod(): InvoiceDeliveryMethod | null {
    const value = this.form?.controls?.['paymentNoticeDeliveryMethod']?.value;
    return typeof value === 'number' ? value : null;
  }

  get selectedSharedInterestInvoiceDeliveryMethod(): InvoiceDeliveryMethod | null {
    return this.useSharedDeliveryMethod
      ? this.selectedPaymentNoticeDeliveryMethod
      : this.selectedInterestInvoiceDeliveryMethod;
  }

  get showInterestInvoiceEmail(): boolean {
    return this.showInterestInvoiceDelivery && (
      this.selectedSharedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EmailWithPDF
      || this.selectedSharedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EmailWithoutPDF
    );
  }

  get showInterestInvoiceEInvoiceAddress(): boolean {
    return this.showInterestInvoiceDelivery && this.selectedSharedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.EInvoiceB2B;
  }

  get showInterestInvoicePostalAddress(): boolean {
    return this.showInterestInvoiceDelivery && this.isPostalAddressDeliveryMethod(this.selectedSharedInterestInvoiceDeliveryMethod);
  }

  get showDeliveryAddress(): boolean {
    return this.isPostalAddressDeliveryMethod(this.selectedPaymentNoticeDeliveryMethod)
      || this.showInterestInvoicePostalAddress;
  }

  get useSharedDeliveryMethod(): boolean {
    return !this.selectedLeaseAgreementId;
  }

  get selectedLeaseAgreementId(): string | null {
    const value = this.canEditLeaseAgreement
      ? this.form?.controls?.['leaseAgreementId']?.value
      : this.dataContext?.leaseAgreementId;
    return value || null;
  }

  get headerParams(): any {
    return { number: this.dataContext?.number || '' };
  }

  get showInterestInvoiceLink(): boolean {
    return !!this.dataContext?.interestInvoiceId;
  }

  get interestInvoiceUrl(): string | null {
    return this.dataContext?.interestInvoiceId
      ? this.createNavigationUrl(['invoices', 'edit', this.dataContext.interestInvoiceId])
      : null;
  }

  get interestInvoiceLinkText(): string {
    const number = this.dataContext?.interestInvoiceNumber;
    const amount = this.dataContext?.interestInvoiceAmount?.toString();
    const dueDate = this.dataContext?.interestInvoiceDueDate?.toLocaleDateString(this._languageService.currentCultureLang);
    return [number, amount, dueDate].filter(part => !!part).join(' - ');
  }

  get showESignatureErrandLink(): boolean {
    return !!this.dataContext?.eSignatureErrandId;
  }

  get eSignatureErrandUrl(): string | null {
    return this.dataContext?.eSignatureErrandId
      ? this.createNavigationUrl(['esignature-errand', 'edit', this.dataContext.eSignatureErrandId])
      : null;
  }

  get eSignatureErrandLinkTextKey(): string {
    return this.dataContext?.isESignatureCompleted ? 'AMORTIZATION_PLAN.ESIGN_COMPLETED' : 'AMORTIZATION_PLAN.ESIGN_SENT';
  }

  sendForDigitalSigning(): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id) {
      return;
    }

    this._navigation.navigate(['amortization-plan', 'send-for-esign', this.dataContext.id]);
  }

  previewPdf(): void {
    if (!this.dataContext?.id) {
      return;
    }

    const tab = this._nativeWindow.openTab();

    this.AddSubscription(
      this.dataService.getPdf(this.dataContext.id).subscribe({
        next: file => this._nativeWindow.openFile(file, `${this.dataContext.number || this.dataContext.id}.pdf`, tab),
        error: error => {
          tab?.close?.();
          this.messageService.showError(error);
        },
      })
    );
  }

  previewPaymentNoticePdf(paymentNotice: AmortizationPlanPaymentNotice): void {
    if (!paymentNotice?.id) {
      return;
    }

    const tab = this._nativeWindow.openTab();

    this.AddSubscription(
      this.dataService.getPaymentNoticePdf(paymentNotice.id).subscribe({
        next: file => this._nativeWindow.openFile(file, `Betalningsavi_${paymentNotice.sequenceNumber || paymentNotice.ocrNumber || paymentNotice.id}.pdf`, tab),
        error: error => {
          tab?.close?.();
          this.messageService.showError(error);
        },
      })
    );
  }

  approve(): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id) {
      return;
    }

    this.AddSubscription(
      this.messageService.showDialog('AMORTIZATION_PLAN.APPROVE.CONFIRM').subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.AddSubscription(this.dataService.approve(this.dataContext.id).subscribe(plan => this.refresh(plan)));
      })
    );
  }

  withdrawApproval(): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id) {
      return;
    }

    this.AddSubscription(
      this.messageService.showDialog('AMORTIZATION_PLAN.WITHDRAW_APPROVAL.CONFIRM').subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.AddSubscription(this.dataService.withdrawApproval(this.dataContext.id).subscribe(plan => this.refresh(plan)));
      })
    );
  }

  cancelPlan(): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id || !this.canCancelPlan) {
      return;
    }

    this.AddSubscription(
      this._dialogService.open(CancelAmortizationPlanFormComponent, this._injector, null, this.dataContext, false).subscribe(plan => {
        if (plan) {
          this.refresh(plan);
        }
      })
    );
  }

  requestPaymentNoticeDelivery(paymentNotice: AmortizationPlanPaymentNotice): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id || !paymentNotice?.id || !this.canRequestPaymentNoticeDelivery(paymentNotice)) {
      return;
    }

    const confirmKey = paymentNotice.latestDelivery
      ? 'AMORTIZATION_PLAN.PAYMENT_NOTICE.RESEND_CONFIRM'
      : 'AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_CONFIRM';

    this.AddSubscription(
      this.messageService.showDialog(confirmKey).subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.AddSubscription(
          this.dataService.requestPaymentNoticeDelivery(paymentNotice.id).subscribe({
            next: () => {
              this.AddSubscription(this.dataService.get(this.dataContext.id).subscribe(plan => this.refresh(plan)));
            },
            error: error => this.messageService.showError(error),
          })
        );
      })
    );
  }

  registerPaymentNoticePayment(paymentNotice: AmortizationPlanPaymentNotice): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id || !paymentNotice?.id || !this.canRegisterPaymentNoticePayment(paymentNotice)) {
      return;
    }

    this.AddSubscription(
      this._dialogService.open(RegisterPaymentNoticePaymentFormComponent, this._injector, null, paymentNotice, false).subscribe(result => {
        if (result) {
          this.AddSubscription(this.dataService.get(this.dataContext.id).subscribe(plan => this.refresh(plan)));
        }
      })
    );
  }

  linkDocument(): void {
    if (!this.canManageAmortizationPlans || !this.dataContext?.id) {
      return;
    }

    this.AddSubscription(
      this._dialogService.open(AmortizationPlanDocumentFormComponent, this._injector, null, this.dataContext, false).subscribe(result => {
        if (result) {
          this.documentManager?.refresh();
        }
      })
    );
  }

  openInterestInvoice(event: MouseEvent): void {
    event.preventDefault();
    if (!this.dataContext?.interestInvoiceId) {
      return;
    }

    this._navigation.navigate(['invoices', 'edit', this.dataContext.interestInvoiceId], null, event.ctrlKey || event.metaKey);
  }

  openESignatureErrand(event: MouseEvent): void {
    event.preventDefault();
    if (!this.dataContext?.eSignatureErrandId) {
      return;
    }

    this._navigation.navigate(['esignature-errand', 'edit', this.dataContext.eSignatureErrandId], null, event.ctrlKey || event.metaKey);
  }

  onInvoiceRowClicked(event: { context: AmortizationPlanInvoice; event: PointerEvent }): void {
    const invoiceId = event?.context?.invoiceId;
    if (!invoiceId) {
      return;
    }

    this._navigation.navigate(['invoices', 'edit', invoiceId], null, event.event?.ctrlKey || event.event?.metaKey);
  }

  private refresh(data: AmortizationPlan): void {
    this.init(data);

    if (this.isSectionAllowed('logs')) {
      this.AddSubscription(
        this.logService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
          this.logs = res.items;
        })
      );
    }
  }

  private updateInterestInvoiceDeliveryState(): void {
    this.syncSharedDeliveryMethod();

    const deliveryMethodControl = this.interestInvoiceDeliveryGroup.controls['deliveryMethod'];
    const emailControl = this.interestInvoiceDeliveryGroup.controls['email'];
    const eInvoiceAddressControl = this.interestInvoiceDeliveryGroup.controls['eInvoiceAddress'];
    const addressGroup = this.interestInvoiceDeliveryAddressGroup;

    if (!this.showInterestInvoiceDelivery) {
      deliveryMethodControl.clearValidators();
      emailControl.clearValidators();
      eInvoiceAddressControl.clearValidators();
      this.setAddressValidators(false);
    } else {
      deliveryMethodControl.setValidators([Validators.required]);
      emailControl.setValidators(this.showInterestInvoiceEmail ? [Validators.email, Validators.required] : [Validators.email]);
      eInvoiceAddressControl.setValidators(this.showInterestInvoiceEInvoiceAddress ? [Validators.required, this.eInvoiceAddressValidator] : [this.eInvoiceAddressValidator]);
      this.setAddressValidators(this.selectedSharedInterestInvoiceDeliveryMethod === InvoiceDeliveryMethod.Mail);
    }

    deliveryMethodControl.updateValueAndValidity({ emitEvent: false });
    emailControl.updateValueAndValidity({ emitEvent: false });
    eInvoiceAddressControl.updateValueAndValidity({ emitEvent: false });
    Object.values(addressGroup.controls).forEach(control => control.updateValueAndValidity({ emitEvent: false }));
  }

  private setAddressValidators(required: boolean): void {
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['name'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['street'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['postalCode'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['city'], required);
    this.setRequiredValidator(this.interestInvoiceDeliveryAddressGroup.controls['country'], required);
  }

  private setRequiredValidator(control: AbstractControl, required: boolean): void {
    if ('required' in (control as any)) {
      (control as any).required = required;
    }

    if (required) {
      control.addValidators(Validators.required);
    } else {
      control.removeValidators(Validators.required);
    }
  }

  private subscribeToFormStateChanges(): void {
    this._formStateSubscription.unsubscribe();
    this._formStateSubscription = new Subscription();
    this._formStateSubscription.add(this.form.controls['interestRate'].valueChanges.subscribe(() => this.updateInterestInvoiceDeliveryState()));
    this._formStateSubscription.add(this.form.controls['leaseAgreementId'].valueChanges.subscribe(() => this.updateInterestInvoiceDeliveryState()));
    this._formStateSubscription.add(this.form.controls['paymentNoticeDeliveryMethod'].valueChanges.subscribe(() => this.updateInterestInvoiceDeliveryState()));
    this._formStateSubscription.add(
      this.interestInvoiceDeliveryGroup.controls['deliveryMethod'].valueChanges.subscribe(() => this.updateInterestInvoiceDeliveryState())
    );
  }

  private syncSharedDeliveryMethod(): void {
    if (!this.useSharedDeliveryMethod) {
      return;
    }

    const deliveryMethod = this.selectedPaymentNoticeDeliveryMethod;
    const interestInvoiceDeliveryMethodControl = this.interestInvoiceDeliveryGroup.controls['deliveryMethod'];
    if (interestInvoiceDeliveryMethodControl.value !== deliveryMethod) {
      interestInvoiceDeliveryMethodControl.setValue(deliveryMethod, { emitEvent: false });
    }
  }

  private toInterestInvoiceDeliverySaveModel(paymentNoticeDeliveryMethod: InvoiceDeliveryMethod): any {
    const delivery = this.interestInvoiceDeliveryGroup.value;
    const request = this.toInterestInvoiceDeliveryRequest(delivery);
    return this.useSharedDeliveryMethod
      ? {
          ...request,
          deliveryMethod: paymentNoticeDeliveryMethod,
        }
      : request;
  }

  private toInterestInvoiceDeliveryRequest(delivery: any): any {
    return {
      ...delivery,
      address: delivery?.address
        ? {
            ...delivery.address,
            countryCode: delivery.address.country,
          }
        : null,
    };
  }

  private isPostalAddressDeliveryMethod(method: InvoiceDeliveryMethod | null | undefined): boolean {
    return this.postalAddressDeliveryMethods.includes(method);
  }

  private eInvoiceAddressValidator(control: AbstractControl): any {
    if (!control.value) {
      return null;
    }

    return /^([^:]+:[^:]+)$/.test(control.value) ? null : { eInvoiceAddress: { message: 'ERROR.FORMAT' } };
  }

  private loadSelectionData(): void {
    this.AddSubscription(
      forkJoin({
        defaults: this.dataService.getCreateDefaults(),
        articles: this._articleService.getList(),
      }).subscribe(({ defaults, articles }) => {
        this.defaults = defaults;
        this.articleSelection = this.toArticleSelection(articles);
        this.leaseAgreementSelection = this.toLeaseAgreementSelection();
      })
    );
  }

  private toArticleSelection(articles: Article[]): SelectionItem<string>[] {
    const currencyCode = this.dataContext?.totalAmount?.currencyCode;
    return (articles ?? [])
      .filter(article => !currencyCode || !article.currencyCode || article.currencyCode === currencyCode)
      .sort((left, right) => left.toString().localeCompare(right.toString()))
      .map(article => new SelectionItem(article.toString(), article.id));
  }

  private toLeaseAgreementSelection(): SelectionItem<string>[] {
    const items = this.availableLeaseAgreements.map(leaseAgreement => new SelectionItem(leaseAgreement.displayName, leaseAgreement.id));
    return [new SelectionItem('', ''), ...items];
  }

  private createNavigationUrl(commands: any[]): string {
    let containsRoot = false;
    const segments = this._navigation.parameterLessUrl.split('/');
    const root = segments.length > 1 ? decodeURIComponent(segments[1]) : '';

    for (let i = 0; i < commands.length; i++) {
      const item = commands[i];
      if (item === root) {
        containsRoot = true;
        break;
      }

      if (typeof item === 'string' && item.split('/').includes(root)) {
        containsRoot = true;
        break;
      }
    }

    const commandParts = containsRoot ? [...commands] : [root, ...commands];
    return this._router.serializeUrl(this._router.createUrlTree(commandParts.filter(x => x !== '' && x !== undefined && x !== null)));
  }

  canRequestPaymentNoticeDelivery(paymentNotice: AmortizationPlanPaymentNotice): boolean {
    if (!this.canManageAmortizationPlans || this.dataContext?.status === AmortizationPlanStatus.Completed || this.dataContext?.status === AmortizationPlanStatus.Cancelled) {
      return false;
    }

    if (!paymentNotice) {
      return false;
    }

    if (!this.isSendableReminderMethod(paymentNotice.deliveryMethod)) {
      return false;
    }

    const status = paymentNotice.latestDelivery?.status;
    return status === undefined || status === null || status === PaymentNoticeDeliveryStatus.Failed;
  }

  canRegisterPaymentNoticePayment(paymentNotice: AmortizationPlanPaymentNotice): boolean {
    if (!this.canManageAmortizationPlans || this.dataContext?.status === AmortizationPlanStatus.Completed || this.dataContext?.status === AmortizationPlanStatus.Cancelled) {
      return false;
    }

    return !!paymentNotice?.remainingAmount?.IsPositive;
  }

  getPaymentNoticeActionKey(paymentNotice: AmortizationPlanPaymentNotice): string {
    return paymentNotice?.latestDelivery
      ? 'AMORTIZATION_PLAN.PAYMENT_NOTICE.RESEND_ACTION'
      : 'AMORTIZATION_PLAN.PAYMENT_NOTICE.SEND_ACTION';
  }

  getPaymentNoticeStatusKey(paymentNotice: AmortizationPlanPaymentNotice): string {
    return paymentNotice?.latestDelivery
      ? this.getDeliveryStatusKey(paymentNotice.latestDelivery)
      : 'AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_STATUS_NOT_REQUESTED';
  }

  getPaymentNoticeStatusClass(paymentNotice: AmortizationPlanPaymentNotice): string {
    return paymentNotice?.latestDelivery
      ? this.getDeliveryStatusClass(paymentNotice.latestDelivery)
      : 'payment-notice-card__status--idle';
  }

  getDeliveryStatusKey(delivery: AmortizationPlanPaymentNoticeDelivery): string {
    return PaymentNoticeDeliveryStatusToString(delivery.status);
  }

  getDeliveryStatusClass(delivery: AmortizationPlanPaymentNoticeDelivery): string {
    switch (delivery?.status) {
      case PaymentNoticeDeliveryStatus.Sent:
        return 'payment-notice-card__status--sent';

      case PaymentNoticeDeliveryStatus.Failed:
        return 'payment-notice-card__status--failed';

      case PaymentNoticeDeliveryStatus.Retrying:
        return 'payment-notice-card__status--retrying';

      case PaymentNoticeDeliveryStatus.Processing:
        return 'payment-notice-card__status--processing';

      case PaymentNoticeDeliveryStatus.Pending:
      default:
        return 'payment-notice-card__status--pending';
    }
  }

  getReminderDeliveryMethodKey(method: ReminderDeliveryMethod): string {
    return ReminderDeliveryMethodToString(method);
  }

  formatLocalDate(value: any): string {
    return value?.toLocaleDateString?.(this._languageService.currentCultureLang) ?? '';
  }

  formatDateTime(value: Date | null | undefined): string {
    return value ? value.toLocaleString(this._languageService.currentCultureLang) : '';
  }

  private isSendableReminderMethod(method: ReminderDeliveryMethod | null | undefined): boolean {
    return method === ReminderDeliveryMethod.Email
      || method === ReminderDeliveryMethod.Mail
      || method === ReminderDeliveryMethod.Manual;
  }
}
